// /app/brands/$brandId/integrations — OAuth-callback landing route.
//
// Why this file exists: brand-service's Meta OAuth redirect goes to
//
//   https://app.nexusdijital.com/app/brands/{id}/integrations?connected=meta
//
// (per nexus-brand-service/internal/handlers/social.go — the
// redirect_uri is configured at the Meta App Dashboard side and can't
// change without re-submitting App Review). But in the SPA,
// "integrations" is a TAB inside /app/brands/$brandId selected via the
// URL hash (#integrations) — NOT a separate route. Without this file
// the OAuth redirect landed on "Not Found" even though the connection
// succeeded server-side. Operator hit this 2026-05-20.
//
// Fix: this route registers the integrations path, captures any query
// params Meta sent us (connected, error, etc.), and immediately
// navigates to /app/brands/$brandId with the integrations tab forced
// via hash + the same query params preserved. IntegrationsPanel reads
// `?connected=meta` and shows the success toast.

import { useEffect } from "react";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { useI18n } from "../../../lib/i18n";

function IntegrationsRedirect() {
  const { t } = useI18n();
  const { brandId } = useParams({ from: "/app/brands/$brandId_/integrations" });
  const nav = useNavigate();
  const search = useSearch({ from: "/app/brands/$brandId_/integrations" });

  useEffect(() => {
    // Preserve every query param Meta passed back (connected, error,
    // state, etc.). Stringify the search object back into a query
    // string and append the hash that drives tab selection in
    // $brandId.tsx (via tabFromHash).
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(search ?? {})) {
      if (typeof v === "string") qs.set(k, v);
    }
    const querySuffix = qs.toString() ? `?${qs.toString()}` : "";
    // Hard-replace via window.location so the hash is set before the
    // brand detail component mounts and reads tabFromHash().
    window.location.replace(`/app/brands/${brandId}${querySuffix}#integrations`);
  }, [brandId, search, nav]);

  // Brief loading state — usually invisible because the replace fires
  // immediately. If something fails (e.g., search hook throws on a
  // malformed URL) the user sees a meaningful message instead of a
  // blank screen.
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Card>
        <p className="text-secondary text-sm">{t("common.loading")}</p>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/app/brands/$brandId_/integrations")({
  component: IntegrationsRedirect,
  // Accept any string-valued query param so Meta's `?connected=meta`,
  // `?error=...`, `?state=...` all pass through to the IntegrationsPanel.
  validateSearch: (s: Record<string, unknown>) => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(s)) {
      if (typeof v === "string") out[k] = v;
    }
    return out;
  },
});
